import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    // Listen on document because cart updates are dispatched from document
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #onCartUpdate = () => {
    // Let cart section re-render finish, then refetch recommendations
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Safer to query inside dialog if markup gets replaced
        const dialog = this.refs?.dialog;
        const scope = dialog || this;
        scope.querySelector('cart-product-recommendations')?.fetchProducts?.();
      });
    });
  };

  open() {
    this.showDialog();

    const recommendations = this.querySelector('cart-product-recommendations');
    recommendations?.fetchProducts?.();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

var FreeShippingBar = class extends HTMLElement {
  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this._onCartUpdated);
    this._syncFromInlineProgress();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this._onCartUpdated);
  }

  _onCartUpdated = (event) => {
    // CartUpdateEvent schema uses detail.resource; keep fallback for compatibility
    const cart = event.detail?.resource || event.detail?.cart || event.detail;
    if (!cart) return;

    const progress = Math.min(parseFloat(cart.total_price) / this.threshold, 1);

    this.style.setProperty('--progress', progress);
    this.classList.toggle('is-complete', progress >= 1);
  };

  get threshold() {
    return parseFloat(this.getAttribute('threshold')) || 1;
  }

  _syncFromInlineProgress() {
    const progress = parseFloat(getComputedStyle(this).getPropertyValue('--progress'));

    if (!isNaN(progress)) {
      this.classList.toggle('is-complete', progress >= 1);
    }
  }
};

customElements.define('free-shipping-bar', FreeShippingBar);

/**
 * @class
 * @function CartProductRecommendations
 */
class CartProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;

    this.handleCartUpdate = this.fetchProducts.bind(this);
    this.handleSubmit = this.onSubmit.bind(this);

    this.container = null;
    this.prevBtn = null;
    this.nextBtn = null;

    this._scrollHandler = () => this.updateArrowsState();
  }

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);

    // Delegate form submits inside this component (works after re-render too)
    this.addEventListener('submit', this.handleSubmit);

    this.fetchProducts();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
    this.removeEventListener('submit', this.handleSubmit);

    this.teardownCarousel();
  }

  fetchProducts() {
    if (this.isLoading) return;

    const url = this.dataset.url;
    if (!url) return;

    this.isLoading = true;

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;

        const recommendations = html.querySelector('cart-product-recommendations');

        if (recommendations && recommendations.innerHTML.trim().length > 0) {
          // Replace markup
          this.innerHTML = recommendations.innerHTML;
          this.classList.add('cart-product-recommendations--loaded');

          // Re-bind carousel every time because innerHTML replacement swaps nodes
          this.setupNativeCarousel();
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  /** AJAX add to cart for forms inside the recommendations */
  async onSubmit(e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    // Only intercept the recommendation ATC forms
    if (!form.matches('form[action="/cart/add"], form[action^="/cart/add"]')) return;

    e.preventDefault();

    const btn = form.querySelector('button[type="submit"]');
    btn?.setAttribute('aria-busy', 'true');
    if (btn) btn.disabled = true;

    try {
      const formData = new FormData(form);

      // Shopify AJAX add endpoint
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      if (!res.ok) throw new Error('Add to cart failed');
      const addedItem = await res.json();

      // Fetch fresh cart to include in event detail
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();

      // Dispatch theme cart update event WITHOUT depending on CartUpdateEvent class export
      document.dispatchEvent(
        new CustomEvent(ThemeEvents.cartUpdate, {
          bubbles: true,
          composed: true,
          detail: {
            resource: cart, // matches theme event schema
            cart,           // extra compatibility for any listeners using detail.cart
            sourceId: 'cart-product-recommendations',
            data: {
              source: 'recommendations',
              variantId: String(addedItem?.variant_id ?? ''),
              itemCount: cart?.item_count,
            },
          },
        })
      );

      // Also proactively refresh recommendations after DOM settles
      requestAnimationFrame(() => requestAnimationFrame(() => this.fetchProducts()));
    } catch (err) {
      console.error(err);
    } finally {
      btn?.removeAttribute('aria-busy');
      if (btn) btn.disabled = false;
    }
  }

  teardownCarousel() {
    if (this.prevBtn) this.prevBtn.onclick = null;
    if (this.nextBtn) this.nextBtn.onclick = null;
    if (this.container) this.container.removeEventListener('scroll', this._scrollHandler);

    this.container = null;
    this.prevBtn = null;
    this.nextBtn = null;
  }

  setupNativeCarousel() {
    // Clean up previous bindings (because innerHTML replacement swaps nodes)
    this.teardownCarousel();

    this.container = this.querySelector('.cart-product-recommendations');
    this.prevBtn = this.querySelector('.previous');
    this.nextBtn = this.querySelector('.next');

    if (!this.container || !this.prevBtn || !this.nextBtn) return;

    // Ensure container can scroll horizontally
    this.container.style.overflowX = this.container.style.overflowX || 'auto';

    this.prevBtn.onclick = () => this.scrollStep(-1);
    this.nextBtn.onclick = () => this.scrollStep(1);

    this.updateArrowsState();
    this.container.addEventListener('scroll', this._scrollHandler, { passive: true });

    // Also update after layout settles (images can change widths)
    requestAnimationFrame(() => this.updateArrowsState());
    setTimeout(() => this.updateArrowsState(), 250);
  }

  scrollStep(direction) {
    if (!this.container) return;

    const firstItem = this.container.querySelector('.cart-carousel__slide');
    if (!firstItem) return;

    // Use actual slide width including gap
    const itemWidth = firstItem.getBoundingClientRect().width;

    // If you use CSS gap on the container, account for it
    const styles = getComputedStyle(this.container);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;

    this.container.scrollBy({
      left: direction * (itemWidth + gap),
      behavior: 'smooth',
    });
  }

  updateArrowsState() {
    if (!this.container || !this.prevBtn || !this.nextBtn) return;

    const maxScrollLeft = this.container.scrollWidth - this.container.clientWidth;
    const currentScroll = this.container.scrollLeft;

    const atStart = currentScroll <= 1;
    const atEnd = currentScroll >= maxScrollLeft - 1;

    this.prevBtn.disabled = atStart;
    this.prevBtn.classList.toggle('is-disabled', atStart);

    this.nextBtn.disabled = atEnd;
    this.nextBtn.classList.toggle('is-disabled', atEnd);

    // If there isn't enough content to scroll, disable both
    if (maxScrollLeft <= 1) {
      this.prevBtn.disabled = true;
      this.nextBtn.disabled = true;
      this.prevBtn.classList.add('is-disabled');
      this.nextBtn.classList.add('is-disabled');
    }
  }
}

customElements.define('cart-product-recommendations', CartProductRecommendations);